"use client";

import { ChevronLeft, ChevronRight } from "@carbon/react/icons";
import { useState, type ReactNode } from "react";
import { landingCopy, wrapSlideIndex } from "@/lib/ui/landing";

export type LandingCarouselSlide = {
  id: string;
  title: string;
  text: string;
  stage: ReactNode;
};

export function LandingCarousel({
  title,
  lead,
  label,
  slides,
}: {
  title: string;
  lead: string;
  label: string;
  slides: readonly LandingCarouselSlide[];
}) {
  const [index, setIndex] = useState(0);
  const slide = slides[index];
  if (!slide) {
    return null;
  }

  return (
    <section
      className="landing-carousel"
      aria-roledescription="carousel"
      aria-label={label}
    >
      <header className="landing-section-head">
        <h2>{title}</h2>
        <p className="landing-lead">{lead}</p>
      </header>
      <div className="landing-carousel-frame">
        <div className="landing-feature-copy">
          <p className="landing-kicker">
            {String(index + 1).padStart(2, "0")} /{" "}
            {String(slides.length).padStart(2, "0")}
          </p>
          <h3>{slide.title}</h3>
          <p className="landing-lead">{slide.text}</p>
          <div className="landing-carousel-controls">
            {/* Compact carousel chrome — Carbon Button is 2.5rem. */}
            <button
              type="button"
              className="landing-carousel-arrow"
              aria-label={landingCopy.carouselPrev}
              onClick={() =>
                setIndex((current) =>
                  wrapSlideIndex(current, slides.length, -1),
                )
              }
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="landing-carousel-arrow"
              aria-label={landingCopy.carouselNext}
              onClick={() =>
                setIndex((current) =>
                  wrapSlideIndex(current, slides.length, 1),
                )
              }
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="landing-carousel-tabs" role="tablist" aria-label={title}>
            {slides.map((item, itemIndex) => (
              // Slide tabs sit in a text column — Carbon Button would dominate the copy.
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={itemIndex === index}
                className={
                  itemIndex === index
                    ? "landing-carousel-tab is-current"
                    : "landing-carousel-tab"
                }
                onClick={() => setIndex(itemIndex)}
              >
                {item.title}
              </button>
            ))}
          </div>
        </div>
        <div aria-live="polite">{slide.stage}</div>
      </div>
    </section>
  );
}
